import { inject, Injectable, InjectionToken } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { EMPTY, forkJoin, from, Observable } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';

import { RoleRepository } from '@telespot/domain';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { AuthService } from '@telespot/web-core';

export interface RoleData {
  id: string;
  name: string;

  /**
   * Whether the role is available for the current active user.
   * A user that is not an admin will not have the admin role available, for example.
   */
  available: boolean;
}

export type RoleEntity = Omit<RoleData, 'available'>;

export interface RolesState {
  roleEntities: EntityState<RoleEntity>;
  loading: boolean;
  error?: string;
  userRoles: EntityState<string>;
}

export const ROLES_REPOSITORY = new InjectionToken<RoleRepository>('ROLES_REPOSITORY');

@Injectable()
export class RolesStore extends ComponentStore<RolesState> {
  private readonly rolesRepository = inject<RoleRepository>(ROLES_REPOSITORY);
  private readonly authService = inject(AuthService);
  private readonly roleEntitiesAdapter: EntityAdapter<RoleEntity>;
  private readonly userRolesAdapter: EntityAdapter<string>;

  constructor() {
    const roleEntitiesAdapter = createEntityAdapter<RoleEntity>({
      selectId: (role) => role.id,
    });
    const userRolesAdapter = createEntityAdapter<string>({
      selectId: (role) => role,
    });
    super({
      roleEntities: roleEntitiesAdapter.getInitialState(),
      loading: true,
      error: undefined,
      userRoles: userRolesAdapter.getInitialState(),
    });

    this.roleEntitiesAdapter = roleEntitiesAdapter;
    this.userRolesAdapter = userRolesAdapter;
  }

  private readonly roleEntities$ = this.select((state) =>
    this.roleEntitiesAdapter.getSelectors().selectAll(state.roleEntities)
  );
  public readonly userRoles$ = this.select((state) => this.userRolesAdapter.getSelectors().selectAll(state.userRoles));

  private readonly isManager$ = this.select(this.userRoles$, (userRoles) =>
    userRoles.some((role) => role.includes('manager') || role.includes('admin'))
  ).pipe(startWith(false));
  public readonly isAdmin$ = this.select(this.userRoles$, (userRoles) =>
    userRoles.some((role) => role.includes('admin'))
  ).pipe(startWith(false));

  public readonly roles$ = this.select(
    this.roleEntities$,
    this.isManager$,
    this.isAdmin$,
    (roleEntities, isManager, isAdmin) =>
      roleEntities.map((role) => ({
        ...role,
        available: isAdmin || (!role.name.includes('admin') && isManager),
      }))
  );

  public readonly rolesForInvitation$ = this.select(this.roles$, (roles) =>
    roles.filter((role) => role.name !== 'admin' && role.name !== 'guest')
  );
  public readonly loading$ = this.select((state) => state.loading);
  public readonly error$ = this.select((state) => state.error);

  private readonly setError = this.updater((state, error: string) => {
    return { ...state, error: error, loading: false };
  });

  private readonly listRoles = this.updater((state, roles: RoleEntity[]) => {
    return {
      ...state,
      roleEntities: this.roleEntitiesAdapter.setAll(roles, state.roleEntities),
      loading: false,
      error: undefined,
    };
  });

  private readonly setUserRoles = this.updater((state, userRoles: string[]) => {
    return {
      ...state,
      userRoles: this.userRolesAdapter.setAll(userRoles, state.userRoles),
      loading: false,
      error: undefined,
    };
  });

  public readonly fetchRoles = this.effect((action$: Observable<void>) =>
    action$.pipe(
      switchMap(() =>
        forkJoin({
          roles: from(this.rolesRepository.list()),
          userRoles: from(this.userRoles()),
        }).pipe(
          tapResponse(
            ({ roles, userRoles }) => {
              const stateRoles = roles.map((role) => ({
                id: role.id ?? '',
                name: role.name,
              }));

              // update the state
              this.listRoles(stateRoles);
              this.setUserRoles(userRoles);
            },
            (error: Error) => {
              this.setError(error.message);
              return EMPTY;
            }
          )
        )
      )
    )
  );

  private async userRoles(): Promise<string[]> {
    const roles = await this.rolesRepository.getUserRoles(this.authService.currentUser?.id);
    return roles.map((role) => role.name);
  }
}
